import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { getPageviewData } from '../../utils/tracking/searchBrowse/getPageviewData';
import get from 'lodash.get';
import SV from 'server-vars';
import Cookies from 'js-cookie';
import { getPublisherTagInstance } from '../../utils/getPublisherTagInstance';
import { trackSortEvent } from '../../utils/tracking/searchBrowse/trackSortEvent';
import { cdnTestTracking } from '../../utils/tracking/searchBrowse/cdnTestTracking';
import { addPersonalizationTrackingData } from '../../utils/tracking/searchBrowse/addPersonalizationTrackingData';
import { trackNullResultsDisplayed } from '../../utils/tracking/searchBrowse/nullResultsTracking';
import { SharedTrackingContainer } from '../../shared/SharedTrackingContainer';
import { pageTypeConstants } from '../../constants/pageTypeConstants';
import tracking from 'dibs-tracking';
import { SharedSaveSearch } from '../../mutations/SharedSaveSearch';
import { USER_AA_TEST_COOKIE } from '../../constants/cookieConstants';
const { SEARCH } = pageTypeConstants;

class SbSharedTrackingContainerComponent extends React.Component {
    constructor() {
        super();

        this.handleTrackingFired = this.handleTrackingFired.bind(this);

        this.state = {
            visitCount: 0,
        };
    }

    componentWillReceiveProps(nextProps) {
        const hasDisplayUriRefChanged = this.hasPropChanged(nextProps, 'itemSearch.displayUriRef');
        const hasUriRefChanged = this.hasPropChanged(nextProps, 'generatedUriRef');
        const sortValuePath = 'itemSearch.sort.currentOption.urlLabel';
        const hasSortValueChanged = this.hasPropChanged(nextProps, sortValuePath);

        /**
         * We're expecting that this will run before any components get notified of a search
         * browse page change.
         */
        if (hasUriRefChanged) {
            const gt = getPublisherTagInstance();
            tracking.resetGtmTracking();

            // FINDING-13256 reset tracking fired in DBL when URL updates w/o hard refresh
            this.props.reinitTracking();

            if (gt) {
                gt.pubads().refresh();
            }
        }

        if (hasDisplayUriRefChanged) {
            this.setState(prevState => {
                return { visitCount: ++prevState.visitCount };
            });
        }

        if (hasSortValueChanged) {
            trackSortEvent(get(nextProps, sortValuePath));
        }
    }

    trackNullResultsPage() {
        const totalResults = get(this.props, 'itemSearch.totalResults');

        if (totalResults === 0) {
            trackNullResultsDisplayed();
        }
    }

    trackUserSearchHistory() {
        const userId = get(this.props, 'user.serviceId');
        const pageType = get(this.props, 'itemSearch.pageType');
        const displayUriRef = get(this.props, 'itemSearch.displayUriRef');
        const totalResults = get(this.props, 'itemSearch.totalResults');

        const hasResults = totalResults > 0;
        const isSearch = pageType === SEARCH;

        if (isSearch && userId && hasResults) {
            SharedSaveSearch.commit(this.props.relay.environment, {
                type: 'SEARCH_HISTORY',
                page: displayUriRef,
                userId,
            });
        }
    }

    handleTrackingFired() {
        const appliedFilters = get(this.props, 'itemSearch.appliedFilters');

        addPersonalizationTrackingData(appliedFilters);

        this.trackNullResultsPage();

        this.trackUserSearchHistory();
    }

    hasPropChanged(newProps, propName) {
        const oldProp = get(this.props, propName);
        const newProp = get(newProps, propName);
        return oldProp && oldProp !== newProp;
    }

    getGtmPageInfo() {
        const {
            itemSearch,
            pageNumber,
            gtmPageInfo = {},
            refineMenuFiltersMap,
            semanticSearchTerm,
            user,
        } = this.props;
        const { pageType, totalResults, appliedFilters } = itemSearch;
        const isVerifiedTrade = get(user, 'isVerifiedTrade');
        const showCategoryTiles = !get(itemSearch, 'browseContentModule');

        const isModule = get(itemSearch, 'browseContentModule');
        const additional = {
            ...gtmPageInfo.additional,
            ...getPageviewData({
                semanticSearchTerm,
                pageType,
                appliedFilters,
                totalResults,
                pageNumber,
                optPageURL: get(this.props, 'itemSearch.displayUriRef'),
                sortValue: get(this.props, 'itemSearch.sort.currentOption.urlLabel'),
                isModule,
                refineMenuFiltersMap,
                topCategoryL1: get(this.props, 'itemSearch.topCategoryL1'),
            }),
        };

        // track categoryTiles when they are visible and for non-trade users only
        if (showCategoryTiles && !isVerifiedTrade) {
            additional.categoryTilesVisible = get(itemSearch, 'categoryTiles.length') ? 'y' : 'n';
        }

        return { ...gtmPageInfo, additional };
    }

    render() {
        const additional = {
            ...this.props.additional,
            ...cdnTestTracking(),
            pageVariant: SV.get('pageVariant'),
            userVariant: Cookies.get(USER_AA_TEST_COOKIE),
        };

        return (
            <SharedTrackingContainer
                visitCount={this.state.visitCount}
                onTrackingFired={this.handleTrackingFired}
                gtmPageInfo={this.getGtmPageInfo()}
                user={this.props.user}
                currency={this.props.currency}
                additional={additional}
                item={null}
                mixpanelTrackingData={{
                    pageViewName: 'results pageview',
                }}
                shouldFireTrackingAdditionalCheck={() => this.props.hasCheckedForSemanticSearchTerm}
            />
        );
    }
}

const mapStateToProps = ({ filters }) => {
    const { generatedUriRef, page } = filters;
    return {
        semanticSearchTerm: get(filters, 'semanticSearchTerm'),
        hasCheckedForSemanticSearchTerm: get(filters, 'hasCheckedForSemanticSearchTerm'),
        pageNumber: page,
        generatedUriRef,
    };
};

SbSharedTrackingContainerComponent.propTypes = {
    relay: PropTypes.object.isRequired,
    user: PropTypes.object,
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    gtmPageInfo: PropTypes.object,
    refineMenuFiltersMap: PropTypes.object.isRequired,
    pageNumber: PropTypes.number.isRequired,
    additional: PropTypes.object,
    // TODO INFRA: Should be required
    generatedUriRef: PropTypes.string,
    reinitTracking: PropTypes.func,
    hasCheckedForSemanticSearchTerm: PropTypes.bool,
    semanticSearchTerm: PropTypes.string,
};

SbSharedTrackingContainerComponent.defaultProps = {
    reinitTracking: () => {},
};

export const SbSharedTrackingContainer = createFragmentContainer(
    connect(mapStateToProps)(SbSharedTrackingContainerComponent),
    {
        itemSearch: graphql`
            fragment SbSharedTrackingContainer_itemSearch on ItemSearchQueryConnection {
                topCategoryL1
                pageType
                totalResults
                displayUriRef
                browseContentModule(contentModuleId: $contentModuleId) {
                    moduleType
                }
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
                sort {
                    currentOption {
                        urlLabel
                    }
                }
                categoryTiles {
                    displayName
                }
            }
        `,
        user: graphql`
            fragment SbSharedTrackingContainer_user on User {
                serviceId
                isVerifiedTrade
                ...SharedTrackingContainer_user
            }
        `,
    }
);
